import { useMemo } from 'react';
import { useDestinationsInAddress } from '@/abilities/useDestinationsInAddress';
import { useSelector } from 'react-redux';
import { pipe, Option, Array as A } from 'effect';
import { AccessInput, mapAccessInput } from '@/abilities/accessInput';
import { allOf, createDestinationRouteCondition } from '@/abilities/routeCondition';
import { useCompanySettingsAccess } from '@/abilities/companySettings/useCompanySettingsAccess';
import {
  isCompanySettingsDestination,
  type CompanySettingsDestinationKey,
} from '@/abilities/companySettings/vocabulary';
import { waitCompanySettingsRouteLoadedSelector } from '@/domain/router/waitRoutesSelectors';

// From the tab down to its section; empty when no settings route declares the address.
export const useCompanySettingsDestinationsInAddress = (): CompanySettingsDestinationKey[] =>
  useDestinationsInAddress(isCompanySettingsDestination);

export const companySettingsRoute = createDestinationRouteCondition(useCompanySettingsAccess);

// A hidden tab shows the not-found page in place of all of settings, and only the root's guard stands outside the
// settings layout; a locked tab passes here and the layout shows its stub. The address without a tab passes only
// when some tab shows the user its content, since it leads there; an address no settings route declares is not found.
function useCompanySettingsEntry(): AccessInput<boolean> {
  const access = useCompanySettingsAccess();
  const destination = pipe(useCompanySettingsDestinationsInAddress(), A.head, Option.getOrNull);
  return useMemo(
    () =>
      mapAccessInput(access, ({ accessOf }) =>
        pipe(
          Option.fromNullable(destination),
          Option.match({
            onNone: () => false,
            onSome: (d) => (d === 'root' ? accessOf('root') === 'open' : accessOf(d) !== 'hidden'),
          }),
        ),
      ),
    [access, destination],
  );
}

// The settings pages read the configuration company from the store, which keeps the previous company's record until
// the next one arrives; their forms would show and save that company's values.
function useConfigurationCompanyInScope(): AccessInput<boolean> {
  const isInScope = useSelector(waitCompanySettingsRouteLoadedSelector);
  return useMemo(() => (isInScope ? AccessInput.Ready({ value: true }) : AccessInput.Loading()), [isInScope]);
}

export const companySettingsRootRoute = allOf(useCompanySettingsEntry, useConfigurationCompanyInScope);
